/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import get from 'lodash/get';
import update from 'immutability-helper';

import {
  LICATALOG_GET_LIS,
  LICATALOG_GET_LIS_SUCCESS,
  LICATALOG_GET_LIS_FAILURE,
  LICATALOG_SET_SORT,
  LICATALOG_SET_SEARCH,
  LICATALOG_FETCH_SCHEMA,
  LICATALOG_FETCH_SCHEMA_SUCCESS,
  LICATALOG_FETCH_SCHEMA_FAILURE,
  LICATALOG_RESET,
} from 'logicalInterfaceActionTypes';

const getInitialState = () => ({
  lis: {
    isLoading: false,
    data: undefined,
    error: undefined,
  },

  // populated when a logical interface is selected, and we need to pass its schema details
  // on to Formik state
  schema: {
    isLoading: false,
    data: undefined,
    error: undefined,
  },
  sort: 'aza',
  search: undefined,
});

export const logicalInterfaceCatalogReducer = (state = getInitialState(), action) => {
  switch (action.type) {
    case LICATALOG_RESET: {
      return getInitialState(); // TODO: only really need to reset schema (so its not picked up in subsequent onSelects)
    }

    case LICATALOG_GET_LIS: {
      return update(state, {
        lis: {
          isLoading: { $set: true },
          data: { $set: undefined },
          error: { $set: undefined },
        },
      });
    }

    case LICATALOG_GET_LIS_SUCCESS: {
      return update(state, {
        lis: {
          isLoading: { $set: false },
          data: { $set: action.payload },
          error: { $set: undefined },
        },
      });
    }

    case LICATALOG_GET_LIS_FAILURE: {
      return update(state, {
        lis: {
          isLoading: { $set: false },
          data: { $set: undefined },
          error: { $set: action.payload },
        },
      });
    }

    case LICATALOG_FETCH_SCHEMA: {
      return update(state, {
        schema: {
          isLoading: { $set: true },
          data: { $set: undefined },
          error: { $set: undefined },
        },
      });
    }

    case LICATALOG_FETCH_SCHEMA_SUCCESS: {
      return update(state, {
        schema: {
          isLoading: { $set: false },
          data: { $set: action.payload },
          error: { $set: undefined },
        },
      });
    }

    case LICATALOG_FETCH_SCHEMA_FAILURE: {
      return update(state, {
        schema: {
          isLoading: { $set: false },
          data: { $set: undefined },
          error: { $set: action.payload },
        },
      });
    }

    case LICATALOG_SET_SORT: {
      return update(state, {
        sort: { $set: action.sort },

        // and clear data to trigger a refetch with the new sort
        lis: {
          isLoading: { $set: false },
          data: { $set: undefined },
          error: { $set: undefined },
        },
      });
    }

    case LICATALOG_SET_SEARCH: {
      return update(state, {
        search: { $set: action.search },

        // and clear data to trigger a refetch with the new sort
        lis: {
          isLoading: { $set: false },
          data: { $set: undefined },
          error: { $set: undefined },
        },
      });
    }

    default:
      // no-op - might be a table action
      return state;
  }
};

// selectors
export const selectors = {
  getInterfaces: state => get(state, 'logicalInterfaceCatalog.lis'),
  getSort: state => get(state, 'logicalInterfaceCatalog.sort'),
  getSearch: state => get(state, 'logicalInterfaceCatalog.search'),
  getSchema: state => get(state, 'logicalInterfaceCatalog.schema'),
};
